import { CheckCircle2, CreditCard, Lock, QrCode, ScanLine, ShieldCheck, Users } from 'lucide-react'
import { motion } from 'motion/react'
import { Sayac } from '../hareket'
import { Avatar, Buton, Etiket, PANEL_MENU, Tarayici, Telefon } from './cerceve'

const YUMUSAK = [0.22, 1, 0.36, 1] as const

function Cubuk({
  oran,
  gecikme = 0,
  renk = 'bg-brand',
}: {
  oran: number
  gecikme?: number
  renk?: string
}) {
  return (
    <motion.i
      className={`block h-full rounded-[3px] ${renk}`}
      initial={{ width: 0 }}
      whileInView={{ width: `${oran}%` }}
      viewport={{ once: true }}
      transition={{ duration: 0.9, ease: YUMUSAK, delay: gecikme }}
    />
  )
}

function Satir({ children, gecikme = 0 }: { children: React.ReactNode; gecikme?: number }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: -6 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: YUMUSAK, delay: gecikme }}
    >
      {children}
    </motion.div>
  )
}

/* ---------- Özet ---------- */
export function SahneOzet() {
  const aylar = [42, 55, 48, 70, 64, 82, 76, 90]
  return (
    <Tarayici adres="app.akillidernek.com/" menu={PANEL_MENU} aktif="Özet">
      <div className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-semibold">Özet</p>
            <p className="text-[9px] text-ink-3">Kocaeli Endüstri Mühendisleri Derneği</p>
          </div>
          <Buton>Üye ekle</Buton>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {[
            { l: 'Aktif üye', v: 1284, s: '' },
            { l: 'Bu ay tahsilat', v: 184600, s: ' ₺' },
            { l: 'Bekleyen başvuru', v: 7, s: '' },
            { l: 'Gecikmiş aidat', v: 61, s: '' },
          ].map((k, i) => (
            <Satir key={k.l} gecikme={i * 0.06}>
              <div className="rounded-[8px] bg-subtle/70 p-2">
                <p className="text-[9px] text-ink-3">{k.l}</p>
                <p className="text-[13px] font-semibold">
                  <Sayac deger={k.v} sonek={k.s} />
                </p>
              </div>
            </Satir>
          ))}
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-[3fr_2fr]">
          <div className="rounded-[8px] p-2 ring-1 ring-line">
            <p className="text-[9px] font-medium">Aylık tahsilat</p>
            <div className="mt-2 flex h-16 items-end gap-1">
              {aylar.map((a, i) => (
                <motion.i
                  key={i}
                  className="block flex-1 rounded-t-[3px] bg-brand/80"
                  initial={{ height: 0 }}
                  whileInView={{ height: `${a}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, ease: YUMUSAK, delay: 0.1 + i * 0.05 }}
                />
              ))}
            </div>
          </div>
          <div className="rounded-[8px] p-2 ring-1 ring-line">
            <p className="text-[9px] font-medium">Bekleyen başvurular</p>
            <ul className="mt-1.5 space-y-1">
              {['Zeynep Kaya', 'Murat Demir', 'Elif Şahin'].map((a, i) => (
                <Satir key={a} gecikme={0.2 + i * 0.08}>
                  <li className="flex items-center gap-1.5 text-[9px]">
                    <Avatar ad={a} />
                    <span className="flex-1">{a}</span>
                    <Etiket tone="info">Başvuru</Etiket>
                  </li>
                </Satir>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Üyeler ---------- */
export function SahneUyeler() {
  const uyeler: Array<[string, string, 'success' | 'info' | 'warning' | 'neutral', string]> = [
    ['Ayşe Yılmaz', 'Asil', 'success', 'Ödendi'],
    ['Mehmet Çelik', 'Asil', 'success', 'Ödendi'],
    ['Zeynep Kaya', 'Öğrenci', 'info', 'Başvuru'],
    ['Ali Öztürk', 'Fahri', 'warning', 'Gecikmiş'],
    ['Deniz Arslan', 'Asil', 'neutral', 'Pasif'],
  ]
  return (
    <Tarayici adres="app.akillidernek.com/uyeler" menu={PANEL_MENU} aktif="Üyeler">
      <div className="p-3 sm:p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-6 flex-1 items-center rounded-[5px] bg-subtle px-2 text-[9px] text-ink-3 ring-1 ring-line">
            Ad, e-posta ya da üye no
          </span>
          <Etiket tone="neutral">Tüm durumlar</Etiket>
          <Buton ikincil>İçe aktar</Buton>
        </div>
        <table className="mt-3 w-full text-[9px]">
          <thead className="text-left text-ink-3">
            <tr className="border-b border-line">
              <th className="py-1 font-medium">Üye</th>
              <th className="py-1 font-medium">Tip</th>
              <th className="py-1 font-medium">Aidat</th>
              <th className="py-1 text-right font-medium">No</th>
            </tr>
          </thead>
          <tbody>
            {uyeler.map(([ad, tip, ton, durum], i) => (
              <motion.tr
                key={ad}
                className="border-b border-line/60"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 + i * 0.07 }}
              >
                <td className="flex items-center gap-1.5 py-1.5">
                  <Avatar ad={ad} />
                  {ad}
                </td>
                <td>{tip}</td>
                <td>
                  <Etiket tone={ton}>{durum}</Etiket>
                </td>
                <td className="data text-right text-ink-3">{1040 + i * 37}</td>
              </motion.tr>
            ))}
          </tbody>
        </table>
        <motion.div
          className="mt-2 flex items-center justify-between rounded-[6px] bg-brand-subtle px-2 py-1.5 text-[9px] text-brand"
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
        >
          <span>2 üye seçildi</span>
          <span className="flex gap-1.5">
            <Buton ikincil>E-posta gönder</Buton>
            <Buton>Durum değiştir</Buton>
          </span>
        </motion.div>
      </div>
    </Tarayici>
  )
}

/* ---------- Aidat ---------- */
export function SahneAidat() {
  const adimlar = [
    { b: 'Borç oluştu', a: '1 Ocak · 2026 aidatı · 1.200 ₺', t: 'neutral' as const },
    { b: 'Hatırlatma gönderildi', a: '25 Ocak · e-posta açıldı', t: 'info' as const },
    { b: 'Kartla ödendi', a: '26 Ocak · PayTR · 1.200 ₺', t: 'success' as const },
    { b: 'Makbuz kesildi', a: 'No 2026-0412 · üyeye e-postayla', t: 'success' as const },
  ]
  return (
    <Tarayici adres="app.akillidernek.com/aidat" menu={PANEL_MENU} aktif="Aidat">
      <div className="grid gap-3 p-3 sm:grid-cols-[1fr_1fr] sm:p-4">
        <div>
          <p className="text-[11px] font-semibold">Ayşe Yılmaz · ekstre</p>
          <ol className="mt-2 space-y-2 border-l border-line pl-3">
            {adimlar.map((s, i) => (
              <Satir key={s.b} gecikme={i * 0.18}>
                <li className="relative">
                  <i
                    className="absolute -left-[17px] top-1 size-2 rounded-full bg-brand ring-2 ring-surface"
                    aria-hidden
                  />
                  <p className="text-[10px] font-medium">{s.b}</p>
                  <p className="text-[9px] text-ink-3">{s.a}</p>
                </li>
              </Satir>
            ))}
          </ol>
        </div>
        <motion.div
          className="rounded-[8px] bg-subtle/60 p-3 ring-1 ring-line"
          initial={{ opacity: 0, scale: 0.97 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <p className="text-[9px] text-ink-3">Tarife · Asil üye</p>
          <p className="data text-[16px] font-semibold">1.200 ₺ / yıl</p>
          <p className="mt-1 text-[9px] text-ink-3">
            Geçerlilik 01.01.2026 · önceki tarife 1.000 ₺ saklandı
          </p>
          <div className="mt-3 space-y-1.5 text-[9px]">
            <div className="flex justify-between">
              <span>Ödeyen</span>
              <span className="data">1.104 / 1.284</span>
            </div>
            <div className="h-1.5 w-full rounded-[3px] bg-line">
              <Cubuk oran={86} gecikme={0.7} />
            </div>
            <div className="flex justify-between text-ink-3">
              <span>Hatırlatma dizisi</span>
              <span>−7 gün · vade · +10 gün</span>
            </div>
          </div>
          <div className="mt-3 flex gap-1.5">
            <Buton>Gecikenlere hatırlat</Buton>
            <Buton ikincil>Rapor</Buton>
          </div>
        </motion.div>
      </div>
    </Tarayici>
  )
}

/* ---------- Web sitesi ---------- */
export function SahneWebSitesi() {
  return (
    <Tarayici adres="app.akillidernek.com/web-sitesi" menu={PANEL_MENU} aktif="Web sitesi">
      <div className="grid gap-3 p-3 sm:grid-cols-[120px_1fr] sm:p-4">
        <div className="space-y-1">
          <p className="text-[9px] font-medium text-ink-3">Bloklar</p>
          {['Üst bant', 'Duyurular', 'Yönetim kurulu', 'Belgeler', 'Etkinlik takvimi', 'Bağış'].map(
            (b, i) => (
              <Satir key={b} gecikme={i * 0.06}>
                <div className="rounded-[5px] bg-subtle px-2 py-1 text-[9px] ring-1 ring-line">
                  {b}
                </div>
              </Satir>
            ),
          )}
          <div className="pt-2">
            <p className="flex items-center gap-1 text-[9px] text-ink-3">
              <Lock className="size-2.5" /> kemd.org.tr
            </p>
            <Etiket tone="success">Sertifika alındı</Etiket>
          </div>
        </div>
        <motion.div
          className="overflow-hidden rounded-[8px] ring-1 ring-line"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.6 }}
        >
          <div className="flex items-center justify-between bg-brand px-3 py-2 text-on-brand">
            <span className="text-[10px] font-semibold">KEMD</span>
            <span className="flex gap-2 text-[8px] opacity-90">
              <span>Hakkımızda</span>
              <span>Duyurular</span>
              <span>Üyelik</span>
            </span>
          </div>
          <div className="bg-surface p-3">
            <p className="heading text-[13px]">2026 Olağan Genel Kurul Çağrısı</p>
            <p className="mt-1 text-[9px] leading-snug text-ink-2">
              Derneğimizin olağan genel kurulu 21 Mart 2026 Cumartesi saat 10.00’da dernek
              merkezinde toplanacaktır.
            </p>
            <div className="mt-2 grid grid-cols-3 gap-1.5">
              {['Gündem', 'Faaliyet raporu', 'Bütçe'].map((d) => (
                <div key={d} className="rounded-[5px] bg-subtle px-2 py-1.5 text-[8px]">
                  {d}.pdf
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </Tarayici>
  )
}

/* ---------- İletişim ---------- */
export function SahneIletisim() {
  const huni = [
    { l: 'Gönderildi', v: 1284, o: 100 },
    { l: 'İletildi', v: 1259, o: 98 },
    { l: 'Açıldı', v: 812, o: 63 },
    { l: 'Tıklandı', v: 394, o: 31 },
  ]
  return (
    <Tarayici adres="app.akillidernek.com/iletisim" menu={PANEL_MENU} aktif="İletişim">
      <div className="grid gap-3 p-3 sm:grid-cols-2 sm:p-4">
        <div className="rounded-[8px] p-2.5 ring-1 ring-line">
          <p className="text-[10px] font-semibold">Kongre daveti</p>
          <p className="text-[9px] text-ink-3">Hedef: aktif üyeler, borcu olmayanlar</p>
          <div className="mt-2 flex flex-wrap gap-1">
            <Etiket tone="neutral">E-posta</Etiket>
            <Etiket tone="neutral">SMS · 3 gün sonra</Etiket>
            <Etiket tone="success">1.284 kişi</Etiket>
          </div>
          <div className="mt-2 rounded-[5px] bg-subtle p-2 text-[9px] leading-snug text-ink-2">
            Sayın {'{{ad}}'}, 14. Ulusal Kongre kayıtları açıldı. Üye fiyatıyla kayıt için…
          </div>
          <div className="mt-2">
            <Buton>Gönder</Buton>
          </div>
        </div>
        <div className="rounded-[8px] p-2.5 ring-1 ring-line">
          <p className="text-[10px] font-semibold">Teslimat</p>
          <ul className="mt-2 space-y-1.5">
            {huni.map((h, i) => (
              <li key={h.l} className="text-[9px]">
                <div className="flex justify-between">
                  <span>{h.l}</span>
                  <span className="data text-ink-3">{h.v.toLocaleString('tr-TR')}</span>
                </div>
                <div className="mt-0.5 h-1.5 rounded-[3px] bg-line">
                  <Cubuk oran={h.o} gecikme={0.15 + i * 0.12} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Portal + mobil ---------- */
export function SahnePortal() {
  return (
    <div className="relative flex items-end justify-center gap-4">
      <Tarayici adres="kemd.akillidernek.com/uyeligim" className="hidden w-[380px] sm:block">
        <div className="p-4">
          <p className="text-[11px] font-semibold">Üyeliğim</p>
          <div className="mt-2 flex items-center justify-between rounded-[8px] bg-subtle p-2.5">
            <div>
              <p className="text-[9px] text-ink-3">Açık borç</p>
              <p className="data text-[14px] font-semibold">1.200 ₺</p>
            </div>
            <Buton>Kartla öde</Buton>
          </div>
          <ul className="mt-2 space-y-1 text-[9px]">
            <li className="flex justify-between">
              <span>Makbuz 2025-0311</span>
              <span className="text-ink-3">PDF</span>
            </li>
            <li className="flex justify-between">
              <span>Destek · Adres güncelleme</span>
              <Etiket tone="success">Yanıtlandı</Etiket>
            </li>
          </ul>
        </div>
      </Tarayici>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: YUMUSAK, delay: 0.2 }}
      >
        <Telefon>
          <div className="p-3 pt-8">
            <div className="rounded-[14px] bg-brand p-3 text-on-brand">
              <p className="text-[8px] opacity-80">KEMD · Asil üye</p>
              <p className="mt-0.5 text-[12px] font-semibold">Ayşe Yılmaz</p>
              <div className="mt-3 flex items-end justify-between">
                <span className="data text-[9px] opacity-90">No 1040 · 2026</span>
                <span className="grid size-10 place-items-center rounded-[6px] bg-on-brand text-brand">
                  <QrCode className="size-7" />
                </span>
              </div>
            </div>
            <div className="mt-3 space-y-1.5">
              {[
                ['Aidat', 'Ödendi', 'success'],
                ['14. Ulusal Kongre', 'Kayıtlı', 'info'],
                ['Genel kurul', '21 Mart', 'neutral'],
              ].map(([a, b, t]) => (
                <div
                  key={a}
                  className="flex items-center justify-between rounded-[8px] bg-surface px-2.5 py-2 text-[9px] ring-1 ring-line"
                >
                  <span>{a}</span>
                  <Etiket tone={t as 'success'}>{b}</Etiket>
                </div>
              ))}
            </div>
          </div>
        </Telefon>
      </motion.div>
    </div>
  )
}

/* ---------- Kurullar / genel kurul ---------- */
export function SahneKurullar() {
  const oy = [
    { l: 'Kabul', v: 612, o: 78, r: 'bg-success' },
    { l: 'Ret', v: 121, o: 15, r: 'bg-danger' },
    { l: 'Çekimser', v: 54, o: 7, r: 'bg-ink-3' },
  ]
  return (
    <Tarayici adres="app.akillidernek.com/kurullar" menu={PANEL_MENU} aktif="Kurullar">
      <div className="grid gap-3 p-3 sm:grid-cols-2 sm:p-4">
        <div>
          <p className="text-[10px] font-semibold">Yönetim kurulu · 2025–2027</p>
          <p className="text-[9px] text-ink-3">Toplantı 14 · 3 Şubat 2026</p>
          <ol className="mt-2 space-y-1.5">
            {[
              ['Karar 41', 'Zeynep Kaya’nın üyelik başvurusunun kabulü'],
              ['Karar 42', '2026 aidat tarifesinin 1.200 ₺ olarak belirlenmesi'],
              ['Karar 43', 'Olağan genel kurulun 21 Mart’a çağrılması'],
            ].map(([n, k], i) => (
              <Satir key={n} gecikme={i * 0.12}>
                <li className="rounded-[6px] bg-subtle/70 px-2 py-1.5 text-[9px]">
                  <span className="data mr-1.5 text-ink-3">{n}</span>
                  {k}
                </li>
              </Satir>
            ))}
          </ol>
        </div>
        <div className="rounded-[8px] p-2.5 ring-1 ring-line">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold">Gündem 4 · Bütçe</p>
            <Etiket tone="danger">Canlı oylama</Etiket>
          </div>
          <p className="mt-0.5 flex items-center gap-1 text-[9px] text-ink-3">
            <Users className="size-2.5" /> Hazirun 787 · salon 412 · uzaktan 375
          </p>
          <ul className="mt-2 space-y-1.5">
            {oy.map((o, i) => (
              <li key={o.l} className="text-[9px]">
                <div className="flex justify-between">
                  <span>{o.l}</span>
                  <span className="data text-ink-3">{o.v}</span>
                </div>
                <div className="mt-0.5 h-1.5 rounded-[3px] bg-line">
                  <Cubuk oran={o.o} gecikme={0.3 + i * 0.12} renk={o.r} />
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Gelir-gider ---------- */
export function SahneMuhasebe() {
  const kalemler: Array<[string, string, string, boolean]> = [
    ['Aidat tahsilatı', 'Ocak', '184.600 ₺', true],
    ['Kongre sponsorluğu', 'Ocak', '150.000 ₺', true],
    ['Salon kirası', 'Ocak', '−42.000 ₺', false],
    ['Personel', 'Ocak', '−68.500 ₺', false],
  ]
  return (
    <Tarayici adres="app.akillidernek.com/gelir-gider" menu={PANEL_MENU} aktif="Gelir-gider">
      <div className="grid gap-3 p-3 sm:grid-cols-[3fr_2fr] sm:p-4">
        <div>
          <p className="text-[10px] font-semibold">Ocak 2026</p>
          <table className="mt-2 w-full text-[9px]">
            <tbody>
              {kalemler.map(([k, d, t, gelir], i) => (
                <motion.tr
                  key={k}
                  className="border-b border-line/60"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                >
                  <td className="py-1.5">{k}</td>
                  <td className="text-ink-3">{d}</td>
                  <td className={`data text-right ${gelir ? 'text-success' : 'text-ink'}`}>{t}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>
          <motion.div
            className="mt-2 flex items-center gap-2 rounded-[6px] bg-brand-subtle px-2 py-1.5 text-[9px] text-brand"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <CheckCircle2 className="size-3" />
            Banka hareketi: 1.200 ₺ · “AD-1040” → Ayşe Yılmaz ile eşleşti
          </motion.div>
        </div>
        <div className="rounded-[8px] p-2.5 ring-1 ring-line">
          <p className="text-[10px] font-semibold">Bütçe · gerçekleşen</p>
          {[
            ['Aidat', 92],
            ['Etkinlik', 71],
            ['Bağış', 38],
            ['Giderler', 64],
          ].map(([l, o], i) => (
            <div key={l as string} className="mt-2 text-[9px]">
              <div className="flex justify-between">
                <span>{l}</span>
                <span className="data text-ink-3">%{o}</span>
              </div>
              <div className="mt-0.5 h-1.5 rounded-[3px] bg-line">
                <Cubuk oran={o as number} gecikme={0.2 + i * 0.1} />
              </div>
            </div>
          ))}
          <div className="mt-3">
            <Buton ikincil>Mali müşavire aktar</Buton>
          </div>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Bağış ---------- */
export function SahneBagis() {
  return (
    <Tarayici adres="kemd.org.tr/bagis/burs-fonu">
      <div className="grid gap-3 p-4 sm:grid-cols-[3fr_2fr]">
        <div>
          <p className="heading text-[14px]">50 öğrenciye burs</p>
          <p className="mt-1 text-[9px] text-ink-2">
            2026–2027 öğretim yılı için aylık 2.000 ₺ burs fonu.
          </p>
          <div className="mt-3 flex items-end justify-between text-[9px]">
            <span className="data text-[13px] font-semibold text-brand">
              <Sayac deger={864000} sonek=" ₺" />
            </span>
            <span className="text-ink-3">hedef 1.200.000 ₺</span>
          </div>
          <div className="mt-1 h-2 rounded-[3px] bg-line">
            <Cubuk oran={72} gecikme={0.2} />
          </div>
          <div className="mt-3 grid grid-cols-4 gap-1.5">
            {['500 ₺', '1.000 ₺', '2.000 ₺', 'Diğer'].map((t, i) => (
              <span
                key={t}
                className={`rounded-[5px] px-1 py-1.5 text-center text-[9px] ring-1 ${i === 2 ? 'bg-brand text-on-brand ring-brand' : 'ring-line'}`}
              >
                {t}
              </span>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between text-[9px]">
            <span className="flex items-center gap-1.5">
              <i className="inline-block h-3 w-5 rounded-full bg-brand" aria-hidden /> Her ay
              düzenli
            </span>
            <Buton>Bağışla</Buton>
          </div>
        </div>
        <div className="rounded-[8px] bg-subtle/60 p-2.5 ring-1 ring-line">
          <p className="text-[9px] font-medium">Son bağışlar</p>
          <ul className="mt-1.5 space-y-1.5">
            {[
              ['Hakan T.', '2.000 ₺'],
              ['İsimsiz', '500 ₺'],
              ['Meltem A.', '1.000 ₺'],
              ['Aslan Mühendislik', '25.000 ₺'],
            ].map(([a, t], i) => (
              <Satir key={a} gecikme={0.3 + i * 0.1}>
                <li className="flex items-center gap-1.5 text-[9px]">
                  <Avatar ad={a} />
                  <span className="flex-1">{a}</span>
                  <span className="data">{t}</span>
                </li>
              </Satir>
            ))}
          </ul>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Kongre ---------- */
export function SahneKongre() {
  return (
    <div className="relative flex items-end justify-center gap-4">
      <Tarayici
        adres="app.akillidernek.com/etkinlikler/14-ulusal-kongre"
        className="hidden w-[400px] sm:block"
      >
        <div className="p-4">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold">14. Ulusal Kongre</p>
            <Etiket tone="success">Kayıt açık</Etiket>
          </div>
          <div className="mt-2 grid grid-cols-3 gap-1.5">
            {[
              ['Kayıt', 612],
              ['Konaklama', 340],
              ['Transfer', 188],
            ].map(([l, v]) => (
              <div key={l as string} className="rounded-[6px] bg-subtle p-2">
                <p className="text-[8px] text-ink-3">{l}</p>
                <p className="data text-[12px] font-semibold">
                  <Sayac deger={v as number} />
                </p>
              </div>
            ))}
          </div>
          <p className="mt-3 text-[9px] font-medium">Salon doluluğu · şu an</p>
          {[
            ['A salonu · Açılış', 88],
            ['B salonu · Bildiriler', 54],
            ['Fuaye · Stantlar', 37],
          ].map(([l, o], i) => (
            <div key={l as string} className="mt-1.5 text-[9px]">
              <div className="flex justify-between">
                <span>{l}</span>
                <span className="data text-ink-3">%{o}</span>
              </div>
              <div className="mt-0.5 h-1.5 rounded-[3px] bg-line">
                <Cubuk oran={o as number} gecikme={0.2 + i * 0.1} />
              </div>
            </div>
          ))}
          <div className="mt-3 rounded-[6px] bg-subtle/70 p-2 text-[9px]">
            <p className="font-medium">Sepet · Ayşe Yılmaz</p>
            <div className="mt-1 flex justify-between text-ink-2">
              <span>Üye bileti · erken kayıt</span>
              <span className="data">2.400 ₺</span>
            </div>
            <div className="flex justify-between text-ink-2">
              <span>Otel · 2 gece · çift kişilik</span>
              <span className="data">5.600 ₺</span>
            </div>
            <div className="flex justify-between text-ink-2">
              <span>Transfer · gidiş-dönüş</span>
              <span className="data">600 ₺</span>
            </div>
          </div>
        </div>
      </Tarayici>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: YUMUSAK, delay: 0.2 }}
      >
        <Telefon>
          <div className="p-3 pt-8">
            <p className="text-[9px] text-ink-3">Kapı · A salonu</p>
            <div className="mt-2 grid aspect-square place-items-center rounded-[14px] bg-ink/90 text-page">
              <ScanLine className="size-10 opacity-70" />
            </div>
            <motion.div
              className="mt-3 flex items-center gap-2 rounded-[10px] bg-success/12 p-2.5 text-success"
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 1, duration: 0.4 }}
            >
              <CheckCircle2 className="size-5" />
              <span className="text-[10px] font-medium leading-tight">
                Ayşe Yılmaz
                <br />
                <span className="font-normal opacity-80">Üye · giriş tamam</span>
              </span>
            </motion.div>
            <p className="data mt-2 text-center text-[9px] text-ink-3">
              Bugün 418 giriş · 0 mükerrer
            </p>
          </div>
        </Telefon>
      </motion.div>
    </div>
  )
}

/* ---------- Akademi ---------- */
export function SahneAkademi() {
  return (
    <Tarayici adres="akademi.kemd.org.tr">
      <div className="grid gap-3 p-4 sm:grid-cols-3">
        {[
          {
            b: 'Yalın Üretim Sertifika Programı',
            d: '8 hafta · 4 canlı ders',
            o: 62,
            e: 'Devam ediyor' as const,
            t: 'info' as const,
          },
          {
            b: 'İş Güvenliği Yenileme',
            d: 'Video · 6 modül',
            o: 100,
            e: 'Sertifika hazır',
            t: 'success' as const,
          },
          {
            b: 'Proje Yönetimi Atölyesi',
            d: 'Canlı · bugün 19.00',
            o: 0,
            e: 'Canlı',
            t: 'danger' as const,
          },
        ].map((k, i) => (
          <Satir key={k.b} gecikme={i * 0.12}>
            <div className="rounded-[8px] p-2.5 ring-1 ring-line">
              <div className="h-12 rounded-[6px] bg-brand-subtle" aria-hidden />
              <p className="mt-2 text-[10px] font-semibold leading-tight">{k.b}</p>
              <p className="text-[9px] text-ink-3">{k.d}</p>
              <div className="mt-2 h-1.5 rounded-[3px] bg-line">
                <Cubuk oran={k.o} gecikme={0.3 + i * 0.1} />
              </div>
              <div className="mt-2">
                <Etiket tone={k.t}>{k.e}</Etiket>
              </div>
            </div>
          </Satir>
        ))}
      </div>
    </Tarayici>
  )
}

/* ---------- Mağaza ---------- */
export function SahneMagaza() {
  return (
    <Tarayici adres="app.akillidernek.com/magaza" menu={PANEL_MENU} aktif="Etkinlikler">
      <div className="grid gap-3 p-3 sm:grid-cols-2 sm:p-4">
        <div>
          <p className="text-[10px] font-semibold">Ürünler</p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {[
              ['Yalın Üretim El Kitabı', '420 ₺', 'Stok 38'],
              ['Dernek rozeti', '90 ₺', 'Stok 4'],
              ['Kongre kitapçığı 2025', '150 ₺', 'Stok 120'],
              ['Polo tişört · M', '350 ₺', 'Tükendi'],
            ].map(([a, f, s], i) => (
              <Satir key={a} gecikme={i * 0.08}>
                <div className="rounded-[6px] p-2 ring-1 ring-line">
                  <div className="h-8 rounded-[4px] bg-subtle" aria-hidden />
                  <p className="mt-1.5 text-[9px] font-medium leading-tight">{a}</p>
                  <div className="mt-1 flex items-center justify-between">
                    <span className="data text-[9px]">{f}</span>
                    <Etiket
                      tone={s === 'Tükendi' ? 'danger' : s === 'Stok 4' ? 'warning' : 'neutral'}
                    >
                      {s}
                    </Etiket>
                  </div>
                </div>
              </Satir>
            ))}
          </div>
        </div>
        <div className="rounded-[8px] p-2.5 ring-1 ring-line">
          <p className="text-[10px] font-semibold">Siparişler</p>
          <ul className="mt-2 space-y-1.5 text-[9px]">
            {[
              ['#2041', 'Mehmet Çelik', 'Kargoda', 'info'],
              ['#2040', 'Elif Şahin', 'Hazırlanıyor', 'warning'],
              ['#2039', 'Aslan Mühendislik', 'Teslim edildi', 'success'],
            ].map(([n, a, d, t]) => (
              <li key={n} className="flex items-center gap-2">
                <span className="data text-ink-3">{n}</span>
                <span className="flex-1">{a}</span>
                <Etiket tone={t as 'info'}>{d}</Etiket>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Tarayici>
  )
}

/* ---------- Güvenlik ---------- */
export function SahneGuvenlik() {
  return (
    <Tarayici adres="app.akillidernek.com/denetim" menu={PANEL_MENU} aktif="Ayarlar">
      <div className="grid gap-3 p-3 sm:grid-cols-[3fr_2fr] sm:p-4">
        <div>
          <p className="text-[10px] font-semibold">Denetim kayıtları</p>
          <ul className="mt-2 space-y-1.5 text-[9px]">
            {[
              ['10:42', 'Sayman · Ali Öztürk', 'aidat tarifesi güncellendi', '1.000 → 1.200 ₺'],
              ['10:31', 'Sekreter · Elif Şahin', 'üye durumu değişti', 'başvuru → aktif'],
              ['09:58', 'Başkan · Mehmet Çelik', 'kullanıcı erişimi kapatıldı', 'Deniz Arslan'],
              ['09:12', 'Platform · destek', 'derneğe salt okunur girildi', 'talep #3391'],
            ].map(([s, k, o, d], i) => (
              <Satir key={s} gecikme={i * 0.1}>
                <li className="grid grid-cols-[32px_1fr] gap-2 rounded-[6px] bg-subtle/60 px-2 py-1.5">
                  <span className="data text-ink-3">{s}</span>
                  <span>
                    <span className="font-medium">{k}</span> · {o}
                    <span className="data ml-1 text-ink-3">{d}</span>
                  </span>
                </li>
              </Satir>
            ))}
          </ul>
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-[8px] p-2.5 ring-1 ring-line">
            <ShieldCheck className="size-4 text-brand" />
            <div className="text-[9px]">
              <p className="font-medium">İkinci adım zorunlu</p>
              <p className="text-ink-3">9 yöneticinin 9’u kurdu</p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-[8px] p-2.5 ring-1 ring-line">
            <CreditCard className="size-4 text-brand" />
            <div className="text-[9px]">
              <p className="font-medium">PayTR anahtarı</p>
              <p className="data text-ink-3">••••••••••••7f2a · şifreli</p>
            </div>
          </div>
          <div className="rounded-[8px] p-2.5 ring-1 ring-line">
            <p className="text-[9px] font-medium">Kiracı izolasyonu</p>
            <div className="mt-1.5 grid grid-cols-3 gap-1">
              {['KEMD', 'BÜTAD', 'ECÖZ'].map((d, i) => (
                <motion.div
                  key={d}
                  className={`rounded-[5px] p-1.5 text-center text-[8px] ring-1 ${i === 0 ? 'bg-brand-subtle text-brand ring-brand/30' : 'bg-subtle text-ink-3 ring-line'}`}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                >
                  {d}
                </motion.div>
              ))}
            </div>
            <p className="mt-1 text-[8px] text-ink-3">
              Satır seviyesi güvenlik: bir dernek diğerinin satırını göremez.
            </p>
          </div>
        </div>
      </div>
    </Tarayici>
  )
}
